import { faInfoCircle } from "@fullscript/pro-regular-svg-icons";
import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Avatar, Button, Spacer, Tooltip, Typography, Arrange } from "@aviary";
import type { PractitionerTypeFragment } from "@shared/data/fragments/PractitionerTypeFragment/PractitionerTypeFragment.fragment";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import { StorePlatform } from "@shared/types/StorePlatform";
import { DesignationSearch } from "@unauthenticated/shared/components/DesignationSearch/DesignationSearch";
import { DispensaryNameInput } from "@unauthenticated/shared/components/DispensaryNameInput/DispensaryNameInput";
import { FirstNameInput } from "@unauthenticated/shared/components/FirstNameInput/FirstNameInput";
import { FormWrapper } from "@unauthenticated/shared/components/FormWrapper/FormWrapper";
import { LastNameInput } from "@unauthenticated/shared/components/LastNameInput/LastNameInput";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import { TermsOfServiceCheckbox } from "@unauthenticated/shared/components/TermsOfServiceCheckbox/TermsOfServiceCheckbox";
import { l } from "@unauthenticated/shared/locales/i18n";

import * as styles from "../SimplifySignUp.styles";
import type { SimplifySignUpProps } from "../types";

import { isFormValid } from "./helpers";

interface Props extends SimplifySignUpProps {
  onClick: (e: FormEvent<HTMLFormElement>) => void;
  onBack?: () => void;
  additionalAttributes?: PractitionerAdditionalAttributesType;
}

const PageOne = ({
  errors,
  onClick,
  formData,
  setFormData,
  additionalAttributes,
  ssoErrors,
  onBack,
}: Props) => {
  const { isEmerson } = useSharedGlobalConfig();
  const { t } = useTranslation();
  const { phone } = useBreakpoints();
  const [inputDirty, setInputDirty] = useState({
    firstName: false,
    lastName: false,
    dispensaryName: false,
  });

  const isPeerReferral = !!additionalAttributes?.peerReferralDisplayAttributes;
  const headerText = isEmerson
    ? t(l.practitionerSignUp.Emerson.CreateAccountHeader)
    : t(l.practitionerSignUp.CreateAccountHeader);
  const [formErrors, setFormErrors] = useState(errors);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;

    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));

    if (formErrors?.[name as keyof typeof formErrors]) {
      setFormErrors(prevState => ({ ...prevState, [name]: undefined }));
    }
  };

  const getDefaultDispensaryName = () => {
    const { firstName, lastName } = formData;

    if (!firstName || !lastName) return "";

    const lastChar = lastName[lastName.length - 1];
    const s = lastChar === "s" || lastChar === "S" ? "" : "s";
    const fullName = `${firstName} ${lastName}'${s}`;

    if (isEmerson) {
      return t(l.common.DispensaryNameDefaultWithPlatform, {
        fullName,
        storePlatform: StorePlatform.EMERSON,
      });
    }

    return t(l.common.DispensaryDisplayName, { fullName });
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;

    setInputDirty(prevState => ({ ...prevState, [name]: true }));
  };

  const handleSetDesignationValue = (pracType: PractitionerTypeFragment | null) => {
    setFormData(prevState => ({ ...prevState, practitionerType: pracType }));
  };

  const updateTimestamp = (tosTimestamp: Date | "") => {
    setFormData(prevState => ({
      ...prevState,
      tosTimestamp: tosTimestamp === "" ? "" : tosTimestamp.toISOString(),
    }));
  };

  const renderHeader = () => {
    if (isPeerReferral) {
      const { referrerFirstName, referrerAvatarUrl } =
        additionalAttributes?.peerReferralDisplayAttributes || {};

      return (
        <div css={styles.peerReferralTitle}>
          {!!referrerAvatarUrl && (
            <Avatar
              size="small"
              image={referrerAvatarUrl}
              css={styles.userAvatar}
              label={`${referrerFirstName} avatar`}
            />
          )}
          <Typography type="h3">
            {t(l.practitionerSignUp.peerReferral.signUpTitle, { referrerFirstName })}
          </Typography>
        </div>
      );
    }

    return <Typography type="h2">{headerText}</Typography>;
  };

  const renderNextButton = () => {
    const isButtonDisabled = !isFormValid(formData);

    const nextButton = (
      <Button
        data-e2e="next_button"
        type="submit"
        disabled={isButtonDisabled}
        isFullWidth
        size="medium"
      >
        {t(l.practitionerSignUp.NextSelectSignUpMethod)}
      </Button>
    );

    if (isButtonDisabled) {
      return (
        <Tooltip
          css={styles.nextButtonTooltip}
          tooltipContent={t(l.practitionerSignUp.disabledNextButtonTooltip)}
        >
          {nextButton}
        </Tooltip>
      );
    }

    return nextButton;
  };

  const renderFirstLastNameFields = () => {
    const nameTooltip = (
      <Tooltip
        tooltipContent={t(l.practitionerSignUp.PracNameTooltip)}
        data-testid="prac-name-tooltip"
        css={styles.tooltip}
      >
        <FontAwesomeIcon icon={faInfoCircle} />
      </Tooltip>
    );
    const firstNameInput = (
      <FirstNameInput
        value={formData.firstName}
        onChange={handleChange}
        required
        isDirty={inputDirty.firstName}
        onBlur={handleBlur}
        isLoading={false}
        errors={formErrors?.firstName ?? undefined}
      />
    );

    const lastNameInput = (
      <LastNameInput
        value={formData.lastName}
        onChange={handleChange}
        required
        isDirty={inputDirty.lastName}
        onBlur={handleBlur}
        isLoading={false}
        errors={formErrors?.lastName ?? undefined}
      />
    );

    const desktopLayout = (
      <div css={styles.row}>
        {firstNameInput}
        <Spacer width="oneHalf" />
        {lastNameInput}
        {!isEmerson && nameTooltip}
      </div>
    );

    const mobileLayout = (
      <>
        <div css={styles.row}>
          {firstNameInput}
          {!isEmerson && nameTooltip}
        </div>
        <div css={styles.row}>
          {lastNameInput}
          {!isEmerson && nameTooltip}
        </div>
      </>
    );

    return phone.greaterThan ? desktopLayout : mobileLayout;
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    if (formData.dispensaryName === "") {
      setFormData({
        ...formData,
        dispensaryName: getDefaultDispensaryName(),
      });
    }
    onClick(e);
  };

  const renderSubtitle = () => {
    return isEmerson ? (
      t(l.practitionerSignUp.Emerson.TellUsAboutYourPractice)
    ) : (
      <Trans i18nKey={l.practitionerSignUp.TellUsAboutYourPractice}>
        Tell us a bit about your practice to get started. All accounts are{" "}
        <strong css={styles.freeText}>100% free</strong>
      </Trans>
    );
  };

  const renderDispensaryNameTooltip = () => {
    if (!isEmerson) {
      return (
        <Tooltip
          tooltipContent={t(l.practitionerSignUp.DispensaryNameTooltip)}
          css={styles.tooltip}
          data-testid="dispensary-name-tooltip"
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </Tooltip>
      );
    }
  };

  const renderDesignationSearchTooltip = () => {
    return (
      <Tooltip
        tooltipContent={t(l.practitionerSignUp.DesignationSearchTooltip)}
        css={styles.tooltip}
        data-testid="designation-search-tooltip"
      >
        <FontAwesomeIcon icon={faInfoCircle} />
      </Tooltip>
    );
  };

  const renderCTAs = () => {
    if (onBack) {
      return (
        <Arrange isFullWidth justify="spaceBetween" isWrapped={false} css={styles.ctas}>
          <Button intention="secondary" onClick={onBack}>
            {t(l.sharedCommon.Back)}
          </Button>
          {renderNextButton()}
        </Arrange>
      );
    }

    return renderNextButton();
  };

  return (
    <div css={styles.contentBox}>
      <div>
        {renderHeader()}
        <Typography css={styles.subTitle}>{renderSubtitle()}</Typography>
      </div>

      <SSOErrorBox ssoErrors={ssoErrors ?? ""} />

      <FormWrapper>
        <form onSubmit={onSubmit} css={styles.form} noValidate>
          {renderFirstLastNameFields()}
          <div css={styles.row}>
            <DispensaryNameInput
              value={formData.dispensaryName}
              onChange={handleChange}
              isDirty={inputDirty.dispensaryName}
              onClick={() => setInputDirty(prevState => ({ ...prevState, dispensaryName: true }))}
              placeholder={!isEmerson ? getDefaultDispensaryName() : undefined}
              required={!isEmerson}
            />
            {renderDispensaryNameTooltip()}
          </div>
          <div css={styles.row}>
            <DesignationSearch
              value={formData.practitionerType ?? undefined}
              setValue={handleSetDesignationValue}
              required
              data-e2e="practitioner-type-trigger"
            />
            {renderDesignationSearchTooltip()}
          </div>
          <Spacer height="half" />
          <TermsOfServiceCheckbox
            updateTimestamp={updateTimestamp}
            isChecked={!!formData.tosTimestamp}
          />
          <Spacer height="oneHalf" />
          {renderCTAs()}
        </form>
      </FormWrapper>
    </div>
  );
};

export { PageOne };
